import { backoffDelay, ticketCooldown, ticketUrl } from './wsPolicy'

/**
 * One authenticated WebSocket that keeps itself connected — the engine behind
 * the notification and order-tracking sockets. Pure (everything is injected),
 * so the lifecycle is unit-tested (ManagedSocket.test.js).
 *
 * A JavaScript port of the customer and rider apps' `ManagedSocket.ts`.
 *
 * Guarantees:
 *   • At most one socket and one connect attempt at a time. `start()` while
 *     running does nothing; a stale attempt (stopped, or superseded) is
 *     dropped before it opens anything.
 *   • At most one retry timer; scheduling replaces, never stacks.
 *   • Every connect fetches a fresh single-use ticket; never `?token=`.
 *   • Backoff with jitter; a 429 pauses *every* socket until Retry-After.
 *   • An auth failure refreshes the session at most `maxAuthRefreshes` times,
 *     then stops for good (status `auth_failed`) — no infinite loop.
 *   • The attempt counter resets only after a connection has stayed up for
 *     `stableAfterMs`, so a server that accepts and immediately closes cannot
 *     pin the retry rate at the minimum.
 *   • `stop()` cancels everything: timers, heartbeat, in-flight attempt, socket.
 *   • Optional `maxAttempts`: after that many failed attempts in a row it
 *     stops with status `exhausted`; `kick()` (tab visible, network back) or
 *     `restart()` starts it again.
 *
 * Status: 'idle' | 'connecting' | 'open' | 'retrying' | 'stopped' |
 *         'auth_failed' | 'fatal' | 'exhausted'
 */

export const realClock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: h => clearTimeout(h),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: h => clearInterval(h),
  random: () => Math.random()
}

const OPEN = 1

export class ManagedSocket {
  /**
   * @param {object} options
   * @param {string} options.wsBase                 wss://host
   * @param {string} options.path                   /ws/…/
   * @param {() => Promise<object>} options.getTicket  resolves a TicketResult (wsPolicy.js)
   * @param {() => Promise<boolean>} options.refreshAuth  refresh the session; false when signed out
   * @param {(url: string) => object} options.createSocket
   * @param {(data: unknown) => void} options.onMessage
   * @param {(status: string) => void} [options.onStatus]
   * @param {number[]} [options.fatalCodes]  close codes that never succeed on retry (4003, 4004)
   * @param {number[]} [options.authCodes]   close codes meaning "credentials rejected" (4001, 4401)
   * @param {(code: number, reason: string) => void} [options.onFatal]
   */
  constructor(options) {
    this.o = {
      fatalCodes: [],
      authCodes: [4001, 4401],
      clock: realClock,
      cooldown: ticketCooldown,
      maxAuthRefreshes: 1,
      maxAttempts: Number.POSITIVE_INFINITY,
      stableAfterMs: 10_000,
      baseMs: 1_000,
      capMs: 30_000,
      heartbeatMs: 25_000,
      ...options
    }
    this.running = false
    // Bumped by every connect attempt and by stop(); stale work compares against it.
    this.generation = 0
    this.ws = null
    this.inFlight = false
    this.retryTimer = null
    this.heartbeatTimer = null
    this.attempt = 0
    this.authRefreshes = 0
    this.openedAt = null
    this.status = 'idle'
  }

  get isOpen() {
    return this.ws != null && this.ws.readyState === OPEN && this.status === 'open'
  }

  get currentStatus() {
    return this.status
  }

  get isRunning() {
    return this.running
  }

  /** Failed attempts since the last stable connection (drives "reconnecting…" UI). */
  get attemptCount() {
    return this.attempt
  }

  /** Begin (or keep) connecting. Idempotent. */
  start() {
    if (this.running) return
    this.running = true
    this.attempt = 0
    this.authRefreshes = 0
    void this.connect()
  }

  /** Stop for good: cancel the pending retry, the heartbeat, any in-flight attempt, and the socket. */
  stop() {
    this.running = false
    this.generation += 1
    this.clearRetry()
    this.stopHeartbeat()
    const ws = this.ws
    this.ws = null
    this.openedAt = null
    if (ws) {
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null
      try {
        ws.close(1000)
      } catch {
        /* already closed */
      }
    }
    this.setStatus('stopped')
  }

  /**
   * Connectivity came back / the tab came to the front: try now instead of
   * waiting out the backoff. Does nothing while connecting or connected, and
   * still honours a 429 cooldown. Revives a socket that ran out of attempts,
   * but never one stopped on purpose, by a fatal code or by an auth failure.
   */
  kick() {
    if (!this.running) {
      if (this.status === 'exhausted') this.start()
      return
    }
    if (this.ws || this.inFlight) return
    this.clearRetry()
    void this.connect()
  }

  /** A user-initiated retry: forget the backoff and auth history, then connect. */
  restart() {
    if (this.running) {
      this.attempt = 0
      this.authRefreshes = 0
      this.kick()
      return
    }
    this.start()
  }

  send(data) {
    if (!this.isOpen) return false
    try {
      this.ws.send(data)
      return true
    } catch {
      return false
    }
  }

  async connect() {
    if (!this.running || this.ws || this.inFlight) return
    const { clock, cooldown } = this.o
    const wait = cooldown.remaining(clock.now())
    if (wait > 0) {
      this.scheduleRetry(wait + this.jitter())
      return
    }

    const gen = ++this.generation
    this.inFlight = true
    this.setStatus('connecting')
    let result
    try {
      result = await this.o.getTicket()
    } catch {
      result = { kind: 'transient' }
    } finally {
      this.inFlight = false
    }
    // Stopped or superseded while the ticket was on its way: open nothing.
    if (gen !== this.generation || !this.running) return

    switch (result && result.kind) {
      case 'ok':
        this.open(result.ticket)
        return
      case 'rate_limited': {
        const backoff = this.nextBackoff()
        const hint = result.retryAfterMs
        const delay = hint != null ? Math.max(hint + this.jitter(), backoff) : backoff
        cooldown.hold(clock.now() + delay)
        this.scheduleRetry(delay)
        return
      }
      case 'auth':
        void this.handleAuthFailure()
        return
      default:
        this.scheduleRetry(this.nextBackoff())
    }
  }

  open(ticket) {
    const ws = this.o.createSocket(ticketUrl(this.o.wsBase, this.o.path, ticket))
    this.ws = ws

    ws.onopen = () => {
      if (this.ws !== ws) return
      this.openedAt = this.o.clock.now()
      this.setStatus('open')
      this.startHeartbeat()
    }
    ws.onmessage = ev => {
      if (this.ws !== ws) return
      this.o.onMessage(ev ? ev.data : undefined)
    }
    ws.onerror = () => {
      // onclose follows and decides what happens next.
    }
    ws.onclose = ev => {
      if (this.ws !== ws) return
      this.ws = null
      this.stopHeartbeat()
      const stable = this.openedAt != null && this.o.clock.now() - this.openedAt >= this.o.stableAfterMs
      this.openedAt = null
      if (stable) {
        this.attempt = 0
        this.authRefreshes = 0
      }
      if (!this.running) return

      const code = ev && typeof ev.code === 'number' ? ev.code : 1006
      if (this.o.fatalCodes.includes(code)) {
        this.running = false
        this.setStatus('fatal')
        if (this.o.onFatal) this.o.onFatal(code, ev && typeof ev.reason === 'string' ? ev.reason : '')
        return
      }
      if (this.o.authCodes.includes(code)) {
        void this.handleAuthFailure()
        return
      }
      this.scheduleRetry(this.nextBackoff())
    }
  }

  async handleAuthFailure() {
    if (this.authRefreshes >= this.o.maxAuthRefreshes) {
      this.giveUpOnAuth()
      return
    }
    this.authRefreshes += 1
    const gen = this.generation
    let ok = false
    try {
      ok = await this.o.refreshAuth()
    } catch {
      ok = false
    }
    if (gen !== this.generation || !this.running) return
    if (!ok) {
      this.giveUpOnAuth()
      return
    }
    this.scheduleRetry(this.nextBackoff())
  }

  giveUpOnAuth() {
    this.running = false
    this.clearRetry()
    this.setStatus('auth_failed')
  }

  scheduleRetry(delay) {
    if (!this.running) return
    if (this.attempt > this.o.maxAttempts) {
      this.running = false
      this.clearRetry()
      this.setStatus('exhausted')
      return
    }
    this.clearRetry()
    this.setStatus('retrying')
    this.retryTimer = this.o.clock.setTimeout(() => {
      this.retryTimer = null
      void this.connect()
    }, delay)
  }

  clearRetry() {
    if (this.retryTimer != null) {
      this.o.clock.clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  }

  nextBackoff() {
    const delay = backoffDelay(this.attempt, () => this.o.clock.random(), { baseMs: this.o.baseMs, capMs: this.o.capMs })
    this.attempt += 1
    return delay
  }

  jitter() {
    return Math.round(this.o.clock.random() * 1_000)
  }

  startHeartbeat() {
    this.stopHeartbeat()
    const every = this.o.heartbeatMs
    if (!every || !this.o.heartbeatPayload) return
    const payload = this.o.heartbeatPayload
    this.heartbeatTimer = this.o.clock.setInterval(() => {
      this.send(payload())
    }, every)
  }

  stopHeartbeat() {
    if (this.heartbeatTimer != null) {
      this.o.clock.clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  setStatus(status) {
    if (this.status === status) return
    this.status = status
    if (this.o.onStatus) this.o.onStatus(status)
  }
}

export default ManagedSocket
